/**
 * Canvas import and export utilities
 * Extracted from MyCanvas.js to reduce file size
 */

import LZString from "lz-string";
import { v4 as uuidv4 } from "uuid";
import { reassignIds, reassignTextIds } from "./shapeUpdateUtils";
import { extractBestHttpImageUrlFromClipboardHtml } from "./clipboardUtils";
import { validateImageFile, validateImageUrl } from "./imageFormatUtils";

/**
 * Create a function to handle importing canvas from file
 * @param {Object} params - Parameters for importing canvas
 * @param {Function} params.setAction - Setter for action state
 * @param {string} params.selectAction - Select action constant
 * @param {Function} params.generateMarkerId - Function to generate marker IDs
 * @param {Function} params.setTexts - Setter for texts array
 * @param {Function} params.setImages - Setter for images array
 * @param {Function} params.setVideos - Setter for videos array
 * @param {Function} params.setRectangles - Setter for rectangles array
 * @param {Function} params.setCircles - Setter for circles array
 * @param {Function} params.setArrows - Setter for arrows array
 * @param {Function} params.setLines - Setter for lines array
 * @param {Function} params.setDoubleSidedArrows - Setter for double-sided arrows array
 * @param {Function} params.saveToHistory - Function to save to history
 * @param {Function} params.setIsImportingCanvas - Setter for import loading state
 * @returns {Function} Function to handle canvas import
 */
export const createHandleImportCanvas = ({
  setAction,
  selectAction,
  generateMarkerId,
  setTexts,
  setImages,
  setVideos,
  setRectangles,
  setCircles,
  setArrows,
  setLines,
  setDoubleSidedArrows,
  saveToHistory,
  setIsImportingCanvas,
  onImportedTextsReady,
  texts, // Current texts array - needed to calculate combined state
  images, // Current images array - needed to calculate combined state
  videos, // Current videos array - needed to calculate combined state
  rectangles, // Current rectangles array - needed to calculate combined state
  circles, // Current circles array - needed to calculate combined state
  arrows, // Current arrows array - needed to calculate combined state
  lines, // Current lines array - needed to calculate combined state
  doubleSidedArrows, // Current doubleSidedArrows array - needed to calculate combined state
}) => {
  return (event) => {
    setAction(selectAction);

    const file = event.target.files[0];
    if (!file) return;

    setIsImportingCanvas(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const arrayBuffer = e.target.result;
      try {
        const uint8Array = new Uint8Array(arrayBuffer);
        const decompressedData = LZString.decompressFromUint8Array(uint8Array);
        if (!decompressedData) throw new Error("Decompression failed");

        const json = JSON.parse(decompressedData);

        const reassignIdsLocal = (arr) => reassignIds(arr, generateMarkerId);
        const reassignTextIdsLocal = (texts = []) =>
          reassignTextIds(texts, generateMarkerId, uuidv4);

        const loadedImages = await Promise.all(
          (json.images || []).map(
            (imgData) =>
              new Promise((resolve) => {
                // Extract name and imageNumber from exported data (could be at root or in image_properties)
                // Check root level first, then image_properties, handling empty strings properly
                const exportedName =
                  imgData.name !== undefined && imgData.name !== null
                    ? imgData.name
                    : imgData.image_properties?.name;
                const exportedImageNumber =
                  imgData.imageNumber !== undefined &&
                  imgData.imageNumber !== null
                    ? imgData.imageNumber
                    : imgData.image_properties?.imageNumber;

                // Create base object by spreading imgData first to preserve all properties,
                // then explicitly override with safe defaults and extracted name/imageNumber
                // Exclude image_id from imported data
                const { image_id, ...imgDataWithoutImageId } = imgData;
                const baseImageObject = {
                  ...imgDataWithoutImageId,
                  id: generateMarkerId(), // Generate new ID with marker_ prefix for imported image
                  image: null, // Will be set if image loads successfully
                  originalImage: undefined, // Don't preserve DOM image references
                  compressedImage: undefined, // Don't preserve DOM image references
                  x: imgData.x || 0,
                  y: imgData.y || 0,
                  width: imgData.width || 100,
                  height: imgData.height || 100,
                  // CRITICAL: Explicitly set name and imageNumber to ensure they're preserved
                  // and override any nested values from image_properties
                  ...(exportedName !== undefined &&
                    exportedName !== null && { name: exportedName }),
                  ...(exportedImageNumber !== undefined &&
                    exportedImageNumber !== null && {
                      imageNumber: exportedImageNumber,
                    }),
                };

                // Check for imageDataUrl first (from exports), then fallback to src
                const imageSrc = imgData.imageDataUrl || imgData.src || "";

                if (!imageSrc || imageSrc.startsWith("blob:")) {
                  return resolve(baseImageObject);
                }

                // Data URLs don't need crossOrigin and can't be tainted
                const isDataUrl = imageSrc.startsWith("data:");
                const isHttpUrl = /^https?:/i.test(imageSrc);

                // Try loading with crossOrigin first (for CORS compatibility), then fallback
                const tryLoadImage = (useCrossOrigin) => {
                  return new Promise((resolveLoad) => {
                    const img = new Image();

                    // Set crossOrigin only for http/https URLs (not data URLs)
                    if (useCrossOrigin && isHttpUrl && !isDataUrl) {
                      try {
                        img.crossOrigin = "anonymous";
                      } catch (e) {
                        // Ignore errors setting crossOrigin
                      }
                    }

                    img.onload = () => {
                      resolveLoad({
                        success: true,
                        image: img,
                        width: imgData.width || img.naturalWidth,
                        height: imgData.height || img.naturalHeight,
                      });
                    };

                    img.onerror = (err) => {
                      resolveLoad({ success: false });
                    };

                    img.src = imageSrc;
                  });
                };

                // First try with crossOrigin for http/https URLs, then fallback
                if (isHttpUrl && !isDataUrl) {
                  tryLoadImage(true).then((result) => {
                    if (result.success) {
                      resolve({
                        ...baseImageObject,
                        image: result.image,
                        width: result.width,
                        height: result.height,
                      });
                    } else {
                      // If crossOrigin load failed, try without it (fallback)
                      console.warn(
                        `Image failed to load with crossOrigin, trying without: ${imageSrc.substring(
                          0,
                          50
                        )}...`
                      );
                      tryLoadImage(false).then((fallbackResult) => {
                        if (fallbackResult.success) {
                          // Image loaded without CORS - it will be tainted
                          // Note: We cannot convert tainted images to data URLs
                          // PDF export may fail, but the image will still display
                          console.warn(
                            `Image loaded without CORS support. PDF export may fail for this image: ${imageSrc.substring(
                              0,
                              50
                            )}...`
                          );
                          resolve({
                            ...baseImageObject,
                            image: fallbackResult.image,
                            width: fallbackResult.width,
                            height: fallbackResult.height,
                          });
                        } else {
                          // Image failed to load completely
                          console.warn(
                            `Failed to load image: ${imageSrc.substring(
                              0,
                              50
                            )}...`
                          );
                          resolve(baseImageObject);
                        }
                      });
                    }
                  });
                } else {
                  // For data URLs or non-HTTP URLs, try without crossOrigin
                  tryLoadImage(false).then((result) => {
                    if (result.success) {
                      resolve({
                        ...baseImageObject,
                        image: result.image,
                        width: result.width,
                        height: result.height,
                      });
                    } else {
                      // Image failed to load completely
                      console.warn(
                        `Failed to load image: ${imageSrc.substring(0, 50)}...`
                      );
                      resolve(baseImageObject);
                    }
                  });
                }
              })
          )
        );

        // ✅ Restore canvas - prepare updated arrays
        const updatedTexts = reassignTextIdsLocal(json.texts);
        const updatedImages = loadedImages;
        const updatedVideos = reassignIdsLocal(json.videos);
        const updatedRectangles = reassignIdsLocal(json.rectangles);
        const updatedCircles = reassignIdsLocal(json.circles);
        const updatedArrows = reassignIdsLocal(json.arrows);
        const updatedLines = reassignIdsLocal(json.lines);
        const updatedDoubleSidedArrows = reassignIdsLocal(json.doubleSidedArrows);

        // Calculate combined arrays (existing + imported) for saveToHistory
        const combinedTexts = [...(texts || []), ...updatedTexts];
        const combinedImages = [...(images || []), ...updatedImages];
        const combinedVideos = [...(videos || []), ...updatedVideos];
        const combinedRectangles = [...(rectangles || []), ...updatedRectangles];
        const combinedCircles = [...(circles || []), ...updatedCircles];
        const combinedArrows = [...(arrows || []), ...updatedArrows];
        const combinedLines = [...(lines || []), ...updatedLines];
        const combinedDoubleSidedArrows = [...(doubleSidedArrows || []), ...updatedDoubleSidedArrows];

        // Update state
        setTexts((prev) => [...prev, ...updatedTexts]);
        setImages((prev) => [...prev, ...updatedImages]);
        setVideos((prev) => [...prev, ...updatedVideos]);
        setRectangles((prev) => [...prev, ...updatedRectangles]);
        setCircles((prev) => [...prev, ...updatedCircles]);
        setArrows((prev) => [...prev, ...updatedArrows]);
        setLines((prev) => [...prev, ...updatedLines]);
        setDoubleSidedArrows((prev) => [...prev, ...updatedDoubleSidedArrows]);

        if (typeof onImportedTextsReady === "function") {
          try {
            onImportedTextsReady(updatedTexts);
          } catch (callbackError) {
            console.error(
              "Failed to process imported text updates:",
              callbackError
            );
          }
        }

        // Save to history with state overrides to use the combined state (existing + imported)
        // This ensures we save the state AFTER import, not before
        saveToHistory(
          "import_canvas",
          combinedImages, // currentImagesOverride - combined existing + imported
          combinedArrows, // currentArrowsOverride - combined existing + imported
          combinedDoubleSidedArrows, // currentDoubleSidedArrowsOverride - combined existing + imported
          combinedLines, // currentLinesOverride - combined existing + imported
          combinedTexts, // currentTextsOverride - combined existing + imported
          null, // currentSelectedTextOverride
          null, // currentSelectedTextIdOverride
          combinedRectangles, // currentRectanglesOverride - combined existing + imported
          null, // currentOffCanvasRectanglesOverride
          combinedCircles, // currentCirclesOverride - combined existing + imported
          null, // currentOffCanvasCirclesOverride
          null, // currentOffCanvasArrowsOverride
          null, // currentOffCanvasDoubleSidedArrowsOverride
          null, // currentOffCanvasLinesOverride
          null, // currentOffCanvasTextsOverride
          null  // currentOffCanvasImagesOverride
        );
        setIsImportingCanvas(false);
      } catch (error) {
        setIsImportingCanvas(false);
        alert("Failed to import canvas. Details: " + error.message);
      }
    };

    reader.onerror = () => {
      setIsImportingCanvas(false);
      alert("Failed to read file. Please try again.");
    };

    reader.readAsArrayBuffer(file);
  };
};

/**
 * Create a function to handle drag and drop events
 * @param {Object} params - Parameters for drag and drop
 * @param {Function} params.setIsImageLoading - Setter for image loading state
 * @param {Function} params.setImageLoadingProgress - Setter for image loading progress
 * @param {Function} params.addImage - Function to add an image
 * @param {Function} params.saveToHistory - Function to save to history
 * @param {Function} params.showErrorSnackbar - Function to show error snackbar
 * @returns {Function} Function to handle drop event
 */
export const createHandleDrop = ({
  setIsImageLoading,
  setImageLoadingProgress,
  addImage,
  saveToHistory,
  showErrorSnackbar,
}) => {
  return (event) => {
    event.preventDefault();
    const dt = event.dataTransfer;

    // Prefer a real image URL from drag HTML (e.g. Google Images) over a dropped raster that may
    // include the page transparency checkerboard baked into pixels.
    const dropHtml = dt.getData("text/html");
    if (dropHtml) {
      const urlFromHtml = extractBestHttpImageUrlFromClipboardHtml(dropHtml);
      if (urlFromHtml) {
        const urlValidation = validateImageUrl(urlFromHtml);
        if (urlValidation.isValid) {
          setIsImageLoading(true);
          setImageLoadingProgress(10);
          addImage(urlFromHtml, "", null, null, null, null, null, false, {
            isOnTopLayer: false,
          });
          saveToHistory();
          return;
        }
      }
    }

    // 1. Handle all dropped files
    if (dt.files && dt.files.length > 0) {
      const allFiles = [...dt.files];
      const imageFiles = allFiles.filter((file) =>
        file.type.startsWith("image/")
      );
      const nonImageFiles = allFiles.filter(
        (file) => !file.type.startsWith("image/")
      );

      // Show error for non-image files
      if (nonImageFiles.length > 0) {
        setIsImageLoading(false);
        setImageLoadingProgress(0);
        const fileNames = nonImageFiles.map((f) => f.name);
        const errorMessages = fileNames.map(name => `${name} is not a supported image. Please Upload a JPEG, PNG, WEBP, ICO, AVIF, or SVG File.`).join("\n");
        // Use showErrorSnackbar if available, otherwise fallback to alert
        if (showErrorSnackbar) {
          showErrorSnackbar(errorMessages);
        } else {
          alert(errorMessages);
        }
      }

      if (imageFiles.length > 0) {
        setIsImageLoading(true);
        setImageLoadingProgress(10);

        imageFiles.forEach((file) => {
          // Validate each file before adding (drag-and-drop preserves original file info)
          const validation = validateImageFile(file);
          if (!validation.isValid) {
            setIsImageLoading(false);
            setImageLoadingProgress(0);
            // Use showErrorSnackbar if available, otherwise fallback to alert
            if (showErrorSnackbar) {
              showErrorSnackbar(validation.error);
            } else {
              alert(validation.error);
            }
            return; // Skip this file
          }
          
          const src = URL.createObjectURL(file);
          addImage(src, "", file, null, null, null, null, false, {
            isOnTopLayer: false,
            stripCheckerboard: true,
          });
        });
      }
    }

    // 2. Handle external image URLs (from other browser tabs)
    const uri = dt.getData("text/uri-list") || dt.getData("text/plain");
    if (uri && uri.startsWith("http")) {
      setIsImageLoading(true);
      setImageLoadingProgress(10);
      addImage(uri, "", null, null, null, null, null, false, {
        isOnTopLayer: false,
      });
    }

    // Save to history after dropping elements
    saveToHistory();
  };
};
